<template>
  <InfuseDropdown v-model="items" :ignore-close-on-click="ignoreCloseOnClick" @close="closeFilter">
    <template v-for="item in items" :key="item.name" #[item.name]>
      <div v-if="item.active" class="relative z-10 ml-1 w-64 rounded-lg bg-white p-5 shadow-xs shadow-gray-300">
        <form id="filter" @submit.prevent="apply()">
          <InfuseSelectFilter
            v-if="item.config.type === FilterConfigurationType.SELECT"
            :model-value="item.config.options"
            :negation-enabled="negationEnabled"
            @apply:value="(option: FilterOption, negated: boolean) => setOptionToAdd(item.config, option, negated)"
            @validation-change="(valid: boolean) => (item.valid = valid)"
          />
          <InfuseSearchSelectFilter
            v-if="item.config.type === FilterConfigurationType.SEARCH_SELECT"
            :model-value="item.config.options"
            :negation-enabled="negationEnabled"
            @apply:value="(option: FilterOption, negated: boolean) => setOptionToAdd(item.config, option, negated)"
            @validation-change="(valid: boolean) => (item.valid = valid)"
          />
          <InfuseRadioFilter
            v-if="item.config.type === FilterConfigurationType.RADIO"
            :model-value="item.config.options"
            @apply:value="(option: FilterOption) => setOptionToAdd(item.config, option)"
            @validation-change="(valid: boolean) => (item.valid = valid)"
          />
          <InfuseQueryFilter
            v-if="item.config.type === FilterConfigurationType.QUERY"
            :model-value="item.config.options[0]"
            @apply:value="(option: FilterOption, negated: boolean, value: string) => setOptionToAdd(item.config, option, negated, value)"
            @validation-change="(valid: boolean) => (item.valid = valid)"
          />
          <InfuseNumericalFilter
            v-if="isNumericalType(item.config.type)"
            :model-value="item.config.options[0]"
            :comparison-type="determineComparisonType(item.config.type)"
            @apply:value="(option: FilterOption, negated: boolean, value: string) => setOptionToAdd(item.config, option, negated, value)"
            @validation-change="(valid: boolean) => (item.valid = valid)"
          />
          <InfuseQueryFilter
            v-if="isStringType(item.config.type)"
            :model-value="item.config.options[0]"
            :comparison-type="determineComparisonType(item.config.type)"
            @apply:value="(option: FilterOption, negated: boolean, value: string) => setOptionToAdd(item.config, option, negated, value)"
            @validation-change="(valid: boolean) => (item.valid = valid)"
          />

          <div class="mt-5 flex justify-between gap-3">
            <InfuseButton class="w-full" type="button" color="secondary" form="filter" @click="cancel">
              {{ t('action.cancel') }}
            </InfuseButton>
            <InfuseButton class="w-full" type="submit" form="filter" :disabled="!item.valid">
              {{ t('action.apply') }}
            </InfuseButton>
          </div>
        </form>
      </div>
    </template>
  </InfuseDropdown>
</template>

<script lang="ts" setup>
import {
  InfuseButton,
  InfuseDropdown,
  InfuseNumericalFilter,
  InfuseQueryFilter,
  InfuseRadioFilter,
  InfuseSearchSelectFilter,
  InfuseSelectFilter
} from '@infuse/ui/components';
import { ComparisonType, Filter, FilterConfiguration, FilterConfigurationType, FilterOption } from '@infuse/ui/domain';
import { Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';

type FilterFormItem = {
  name: string;
  label: string;
  active: false;
  config: FilterConfiguration;
  valid: boolean;
}; // filter configs mapped to the dropdown-item interface and form validation status

interface Props {
  modelValue: FilterConfiguration[];
  ignoreCloseOnClick?: HTMLElement[];
  negationEnabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  ignoreCloseOnClick: () => [],
  negationEnabled: true
});

const { t } = useI18n();

interface Emits {
  (e: 'add:filter', filter: Filter): void;

  (e: 'close'): void;
}

const emit = defineEmits<Emits>();

let optionToAdd:
  | { config: FilterConfiguration; selectedOption: FilterOption; negated: boolean; searchQuery?: string }
  | undefined;

const items: Ref<FilterFormItem[]> = ref(
  props.modelValue.map((c) => {
    return { name: c.name, label: c.label(), active: false, config: c, valid: false } as FilterFormItem;
  })
);

function cancel(): void {
  items.value.forEach((i) => (i.active = false)); // de-activate all dropdown items
}

function closeFilter(): void {
  cancel();
  emit('close');
}

function setOptionToAdd(
  config: FilterConfiguration,
  selectedOption: FilterOption,
  negated = false,
  searchQuery?: string
): void {
  optionToAdd = { config, selectedOption, negated, searchQuery };
}

function apply(): void {
  if (optionToAdd) {
    emit('add:filter', { ...optionToAdd } as Filter);
    optionToAdd = undefined;
    closeFilter();
  }
}

function isNumericalType(type: FilterConfigurationType): boolean {
  return (
    type === FilterConfigurationType.NUMERICAL ||
    type === FilterConfigurationType.LT ||
    type === FilterConfigurationType.LTE ||
    type === FilterConfigurationType.GT ||
    type === FilterConfigurationType.GTE
  );
}

function isStringType(type: FilterConfigurationType): boolean {
  return (
    type === FilterConfigurationType.STRING_GT ||
    type === FilterConfigurationType.STRING_LT ||
    type === FilterConfigurationType.STRING_GTE ||
    type === FilterConfigurationType.STRING_LTE
  );
}

function determineComparisonType(type: FilterConfigurationType): ComparisonType {
  if (type === FilterConfigurationType.LT || type === FilterConfigurationType.STRING_LT) {
    return ComparisonType.LT;
  } else if (type === FilterConfigurationType.LTE || type === FilterConfigurationType.STRING_LTE) {
    return ComparisonType.LTE;
  } else if (type === FilterConfigurationType.GT || type === FilterConfigurationType.STRING_GT) {
    return ComparisonType.GT;
  } else if (type === FilterConfigurationType.GTE || type === FilterConfigurationType.STRING_GTE) {
    return ComparisonType.GTE;
  }
  return ComparisonType.EQ;
}
</script>

